<template>
    <CardContentPanel icon="creditcard" v-bind="{action, index}">
        <template #title="{month}">{{ t('projectclient.projectview.activity.pending.actions.PRE_PAY_PROJECT_LOT.title', {0: month}) }}</template>
        <template #body>
            <p v-html="getLabel"></p>
            <VJoyHighlight v-if="isPaymentInProgress" :accent="true" level="info">
                <template #default>
                    {{ $t('projectclient.projectview.activity.pending.actions.PRE_PAY_PROJECT_LOT.awaitingPayment') }}
                </template>
            </VJoyHighlight>
        </template>
        <template #actions>
            <NuxtLink
                :to="`/project-payment/${projectInformationStore.projectInformation.projectId}/lot/${action.extraData.lotId}?origin=project-view`"
                external
                data-testid="pending-action-card-cta-primary"
            >
                <VJoyButton :variant="ctaVariant" size="small">
                    {{ t('projectclient.projectview.activity.pending.actions.PRE_PAY_PROJECT_LOT.action.primary') }}
                </VJoyButton>
            </NuxtLink>
        </template>
    </CardContentPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {addDays} from 'date-fns';
    import capitalize from 'lodash-es/capitalize';
    import {parse} from 'tinyduration';
    import {useFormat, useLogger, useTranslation} from '#imports';
    import type {TJoyButtonVariants} from '@maltjoy/core-vue';
    import {VJoyButton, VJoyHighlight} from '@maltjoy/core-vue';
    import CardContentPanel from '../CardContentPanel.vue';
    import {useProjectInformationStore} from '@/common/stores';
    import type {ActivityPendingActionByType} from '@/features/project-view';

    const props = defineProps<{
        action: ActivityPendingActionByType<'PRE_PAY_PROJECT_LOT'>;
        index: number;
    }>();

    const projectInformationStore = useProjectInformationStore();
    const logger = useLogger();
    const {formatDate, formatAmount} = useFormat();
    const {t} = useTranslation();

    const isPaymentInProgress = computed<boolean>(() => props.action.extraData.paymentInProgress === 'true');

    const ctaVariant = computed<TJoyButtonVariants>(() => (isPaymentInProgress.value ? 'secondary' : 'main'));

    const dueFrom = computed(() => {
        if (props.action.extraData.dueFrom) {
            return formatDate(addDays(new Date(), parse(props.action.extraData.dueFrom).days ?? 0));
        }
        logger.error(
            `Missing "dueFrom" extraData from PRE_PAY_PROJECT_LOT pending action in project ${projectInformationStore.projectInformation.projectId}`,
        );
        return '-';
    });

    const formattedAmount = computed<string>(() => {
        const amount = props.action.extraData.remaining;
        return !isNaN(amount) ? formatAmount(projectInformationStore.projectInformation.contract.currency, amount, {decimals: 2}) : '-';
    });

    const getLabel = computed(() => {
        const {extraData} = props.action;
        const translationKey = `projectclient.projectview.activity.pending.actions.PRE_PAY_PROJECT_LOT.description`;
        if (typeof extraData.billingContact === 'undefined') {
            return t(translationKey, {
                0: formattedAmount.value,
                1: props.action.extraData.estimatedWorkAmount,
                2: projectInformationStore.projectInformation.freelancer.firstName,
            });
        } else {
            return t(`${translationKey}.with.billing.email`, {
                0: formattedAmount.value,
                1: props.action.extraData.estimatedWorkAmount,
                2: projectInformationStore.projectInformation.freelancer.firstName,
            });
        }
    });
</script>
